import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formulaTerms,
  type ReclassImportResult,
  type ReclassItem,
  type ReclassItemRequest,
  type ReclassPlan,
  reclassCsv,
  reclassProblems,
  RECLASS_SIGNS,
  type ReclassSign,
  unmappedBaseItems,
} from '@k-management/shared';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Field } from '../../components/Field.js';
import { Icon } from '../../components/Icon.js';
import { type Command, useCommands, usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';

const plansKey = ['controlling', 'plans'] as const;

/**
 * Controllo di gestione › Piani riclassificati (08/10/2026): the plan Base,
 * matched to registry, invoices and lines, and the plans that gather its
 * items in their own way. Items, aggregates with their formulas, order,
 * import and export in the CSV of the Excel.
 */
export function ReclassPlansPage() {
  usePageTitle('Piani riclassificati');
  const queryClient = useQueryClient();
  const canEdit = useCan('controllo.plans');
  const plans = useQuery({
    queryKey: plansKey,
    queryFn: () => apiRequest<ReclassPlan[]>('/controlling/plans'),
  });
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<ReclassItem | 'new-item' | 'new-aggregate' | null>(null);
  const [creating, setCreating] = useState<'new' | 'copy' | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [removing, setRemoving] = useState<ReclassItem | null>(null);
  const [importing, setImporting] = useState(false);
  const all = plans.data ?? [];
  const plan = all.find((item) => item.id === selected) ?? all[0] ?? null;
  const base = all.find((item) => item.isBase) ?? null;
  const refresh = () => queryClient.invalidateQueries({ queryKey: plansKey });
  const save = (path: string, method: 'PUT' | 'DELETE', body?: unknown) =>
    apiRequest<ReclassPlan>(path, { method, body });
  const order = useMutation({
    mutationFn: (ids: string[]) => save(`/controlling/plans/${plan!.id}/order`, 'PUT', { ids }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (item: ReclassItem) =>
      save(`/controlling/plans/${plan!.id}/items/${item.id}`, 'DELETE'),
    onSuccess: async () => {
      setRemoving(null);
      await refresh();
    },
  });
  const deletePlan = useMutation({
    mutationFn: () => apiRequest(`/controlling/plans/${plan!.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setDeleting(false);
      setSelected(null);
      await refresh();
    },
  });

  useCommands(
    canEdit && plan
      ? [
          {
            key: 'item',
            label: 'Voce',
            icon: 'plus',
            primary: true,
            onClick: () => setEditing('new-item'),
          } satisfies Command,
          {
            key: 'aggregate',
            label: 'Aggregato',
            icon: 'plus',
            onClick: () => setEditing('new-aggregate'),
          } satisfies Command,
          {
            key: 'import',
            label: 'Importa CSV',
            icon: 'upload',
            onClick: () => setImporting(true),
          } satisfies Command,
        ]
      : [],
  );

  const exportCsv = () => {
    if (!plan) return;
    // The BOM lets Excel read the accents of the CSV.
    const blob = new Blob([String.fromCharCode(0xfeff), reclassCsv(plan.items)], {
      type: 'text/csv',
    });
    const link = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(blob),
      download: `Riclassificato_${plan.name.replace(/[\\/:*?"<>|]+/g, ' ')}.csv`,
    });
    link.click();
    URL.revokeObjectURL(link.href);
  };
  const move = (index: number, step: -1 | 1) => {
    if (!plan) return;
    const ids = plan.items.map((item) => item.id);
    const target = index + step;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    order.mutate(ids);
  };
  const problems = plan ? reclassProblems(plan.items) : [];
  const missing = plan && base && !plan.isBase ? unmappedBaseItems(base.items, plan.items) : [];

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>Piani riclassificati</h1>
          <p className="muted">
            Il conto economico gestionale, non la contabilità. Il piano <strong>Base</strong> si
            abbina ad anagrafiche, fatture e righe; gli altri piani raggruppano a modo loro le voci
            del Base, senza abbinamenti da rifare.
          </p>
        </div>
      </div>
      {plans.isError && <p className="error">{plans.error.message}</p>}
      {plan && (
        <>
          <div className="row reclass-tabs">
            {all.map((item) => (
              <button
                key={item.id}
                type="button"
                className={item.id === plan.id ? 'subtab selected' : 'subtab'}
                onClick={() => setSelected(item.id)}
              >
                {item.name}
              </button>
            ))}
            {canEdit && (
              <button type="button" className="link" onClick={() => setCreating('new')}>
                + Nuovo piano
              </button>
            )}
            <span className="spacer" />
            {canEdit && (
              <>
                <button type="button" onClick={() => setRenaming(true)}>
                  Rinomina
                </button>
                <button type="button" onClick={() => setCreating('copy')}>
                  Duplica
                </button>
                {!plan.isBase && (
                  <button type="button" onClick={() => setDeleting(true)}>
                    Togli piano
                  </button>
                )}
              </>
            )}
            <button type="button" onClick={exportCsv} disabled={!plan.items.length}>
              Esporta CSV
            </button>
          </div>
          {plan.items.length === 0 && (
            <p className="notice info">
              Il piano è vuoto: aggiungi le voci, oppure «Importa CSV» con il file dell’Excel
              (CONTO;SEGNO;DESCRIZIONE;FORMULA).
            </p>
          )}
          {problems.length > 0 && (
            <div className="notice small">
              {problems.map((problem) => (
                <div key={`${problem.code}-${problem.message}`}>
                  <strong>{problem.code}</strong>: {problem.message}
                </div>
              ))}
            </div>
          )}
          {missing.length > 0 && (
            <p className="notice small">
              Voci del Base che questo piano non raccoglie: {missing.join(', ')}.
            </p>
          )}
          {plan.items.length > 0 && (
            <div className="table-scroll">
              <table className="data-table reclass-table">
                <thead>
                  <tr>
                    <th>Conto</th>
                    <th>Segno</th>
                    <th>Descrizione</th>
                    <th>Formula</th>
                    <th>{plan.isBase ? 'Usata da' : 'Voci del Base'}</th>
                    {canEdit && <th />}
                  </tr>
                </thead>
                <tbody>
                  {plan.items.map((item, index) => (
                    <tr
                      key={item.id}
                      className={[
                        item.formula ? 'aggregate' : '',
                        item.active ? '' : 'inactive',
                        /^EBITDA$/i.test(item.code) ? 'highlight' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <td className="reclass-code">{item.code}</td>
                      <td>{item.sign === '-' ? '−' : '+'}</td>
                      <td>
                        {item.description}
                        {!item.active && <span className="pill muted">disattivata</span>}
                      </td>
                      <td className="reclass-code">{item.formula ?? ''}</td>
                      <td className="muted small">
                        {plan.isBase
                          ? item.formula
                            ? ''
                            : item.uses
                              ? `${item.uses} ${item.uses === 1 ? 'anagrafica' : 'anagrafiche'}`
                              : '—'
                          : item.baseItems.join(', ')}
                      </td>
                      {canEdit && (
                        <td className="row-actions">
                          <button
                            type="button"
                            className="icon-button"
                            title="Su"
                            aria-label="Su"
                            disabled={index === 0 || order.isPending}
                            onClick={() => move(index, -1)}
                          >
                            <Icon name="up" />
                          </button>
                          <button
                            type="button"
                            className="icon-button"
                            title="Giù"
                            aria-label="Giù"
                            disabled={index === plan.items.length - 1 || order.isPending}
                            onClick={() => move(index, 1)}
                          >
                            <Icon name="down" />
                          </button>
                          <button
                            type="button"
                            className="icon-button"
                            title="Modifica"
                            aria-label="Modifica"
                            onClick={() => setEditing(item)}
                          >
                            <Icon name="edit" />
                          </button>
                          <button
                            type="button"
                            className="icon-button"
                            title="Togli"
                            aria-label="Togli"
                            onClick={() => setRemoving(item)}
                          >
                            <Icon name="close" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {editing && plan && (
        <ItemDialog
          plan={plan}
          base={base}
          item={typeof editing === 'string' ? null : editing}
          aggregate={
            editing === 'new-aggregate' || (typeof editing !== 'string' && !!editing.formula)
          }
          onClose={() => setEditing(null)}
        />
      )}
      {creating && (
        <PlanDialog
          copyFrom={creating === 'copy' ? plan : null}
          onClose={() => setCreating(null)}
          onCreated={(created) => setSelected(created.id)}
        />
      )}
      {renaming && plan && <RenameDialog plan={plan} onClose={() => setRenaming(false)} />}
      {importing && plan && <ImportDialog plan={plan} onClose={() => setImporting(false)} />}
      {deleting && plan && (
        <ConfirmDialog
          title={`Togliere il piano «${plan.name}»?`}
          confirmLabel="Togli"
          busy={deletePlan.isPending}
          error={deletePlan.error?.message}
          onConfirm={() => deletePlan.mutate()}
          onCancel={() => setDeleting(false)}
        >
          <p>
            Le voci del Base e i loro abbinamenti restano; si toglie solo questo raggruppamento.
          </p>
        </ConfirmDialog>
      )}
      {removing && (
        <ConfirmDialog
          title={`Togliere la voce ${removing.code}?`}
          confirmLabel="Togli"
          busy={remove.isPending}
          error={remove.error?.message}
          onConfirm={() => remove.mutate(removing)}
          onCancel={() => setRemoving(null)}
        >
          <p>
            {removing.uses > 0
              ? 'È usata in Anagrafica: viene disattivata, non tolta, e resta nei report del passato.'
              : 'Non è usata: viene tolta dal piano.'}
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}

function useDialog() {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    ref.current?.showModal();
  }, []);
  return ref;
}

function ItemDialog({
  plan,
  base,
  item,
  aggregate,
  onClose,
}: {
  plan: ReclassPlan;
  base: ReclassPlan | null;
  item: ReclassItem | null;
  aggregate: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const ref = useDialog();
  const [form, setForm] = useState({
    code: item?.code ?? '',
    sign: (item?.sign ?? '+') as ReclassSign,
    description: item?.description ?? '',
    formula: item?.formula ?? '',
    baseItems: item?.baseItems ?? [],
    active: item?.active ?? true,
  });
  const save = useMutation({
    mutationFn: () => {
      const body: ReclassItemRequest = {
        code: form.code,
        sign: form.sign,
        description: form.description,
        formula: aggregate ? form.formula : null,
        baseItems: form.baseItems,
        active: form.active,
      };
      return item
        ? apiRequest(`/controlling/plans/${plan.id}/items/${item.id}`, { method: 'PUT', body })
        : apiRequest(`/controlling/plans/${plan.id}/items`, { method: 'POST', body });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: plansKey });
      onClose();
    },
  });
  // The formula checked while it is written, against the plan as it would be.
  const formulaProblem = (() => {
    if (!aggregate) return null;
    if (!form.formula.trim()) return 'Scrivi la formula, ad esempio RL-CVC-CVP';
    if (!formulaTerms(form.formula)) return 'Solo codici uniti da + e −, ad esempio RL-CVC-CVP';
    const next = [
      ...plan.items.filter((other) => other.id !== item?.id),
      { code: form.code || '?', formula: form.formula, active: true },
    ];
    const problem = reclassProblems(next).find(
      (found) => found.message !== 'nessun aggregato la conta',
    );
    return problem ? `${problem.code}: ${problem.message}` : null;
  })();
  const baseAnalytic = (base?.items ?? []).filter((other) => !other.formula && other.active);
  return (
    <dialog ref={ref} className="dialog" onClose={onClose}>
      <div className="stack">
        <h2>
          {item ? `Voce ${item.code}` : aggregate ? 'Nuovo aggregato' : 'Nuova voce'} · {plan.name}
        </h2>
        <div className="form-grid">
          <Field label="Conto" hint="Lettere e cifre: 43, CVP, EBITDA.">
            <input
              value={form.code}
              maxLength={12}
              onChange={(event) => setForm({ ...form, code: event.target.value.trim() })}
            />
          </Field>
          <Field label="Segno" hint="«−» se nel suo gruppo toglie (costi, sconti).">
            <select
              value={form.sign}
              onChange={(event) => setForm({ ...form, sign: event.target.value as ReclassSign })}
            >
              {RECLASS_SIGNS.map((sign) => (
                <option key={sign} value={sign}>
                  {sign === '-' ? '−' : '+'}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Descrizione">
            <input
              value={form.description}
              maxLength={200}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
          </Field>
        </div>
        {aggregate && (
          <Field label="Formula" error={formulaProblem ?? undefined} hint="Codici uniti da + e −.">
            <input
              value={form.formula}
              onChange={(event) => setForm({ ...form, formula: event.target.value })}
            />
          </Field>
        )}
        {!aggregate && !plan.isBase && (
          <Field label="Voci del Base che raccoglie">
            <div className="reclass-base-items">
              {baseAnalytic.map((other) => (
                <label key={other.code} className="check small">
                  <input
                    type="checkbox"
                    checked={form.baseItems.includes(other.code)}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        baseItems: event.target.checked
                          ? [...form.baseItems, other.code]
                          : form.baseItems.filter((code) => code !== other.code),
                      })
                    }
                  />{' '}
                  {other.code} · {other.description}
                </label>
              ))}
            </div>
          </Field>
        )}
        {item && (
          <label className="check reclass-check">
            <input
              type="checkbox"
              checked={!form.active}
              onChange={(event) => setForm({ ...form, active: !event.target.checked })}
            />{' '}
            Disattivata (resta nel passato, non si sceglie più)
          </label>
        )}
        {save.error && <p className="error">{save.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={
              save.isPending || !form.code || !form.description.trim() || Boolean(formulaProblem)
            }
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Attendere…' : 'Salva'}
          </button>
        </div>
      </div>
    </dialog>
  );
}

function PlanDialog({
  copyFrom,
  onClose,
  onCreated,
}: {
  copyFrom: ReclassPlan | null;
  onClose: () => void;
  onCreated: (plan: ReclassPlan) => void;
}) {
  const queryClient = useQueryClient();
  const ref = useDialog();
  const [name, setName] = useState(copyFrom ? `Copia di ${copyFrom.name}` : '');
  const save = useMutation({
    mutationFn: () =>
      apiRequest<ReclassPlan>('/controlling/plans', {
        method: 'POST',
        body: { name, copyFrom: copyFrom?.id ?? null },
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: plansKey });
      onCreated(created);
      onClose();
    },
  });
  return (
    <dialog ref={ref} className="dialog narrow" onClose={onClose}>
      <div className="stack">
        <h2>{copyFrom ? `Duplica «${copyFrom.name}»` : 'Nuovo piano'}</h2>
        <p className="muted small">
          {copyFrom
            ? 'Il nuovo piano parte con le stesse voci; se copi il Base, ogni voce raccoglie la voce del Base con lo stesso codice.'
            : 'Il nuovo piano parte vuoto: le sue voci raccolgono quelle del Base.'}
        </p>
        <Field label="Nome">
          <input value={name} maxLength={80} onChange={(event) => setName(event.target.value)} />
        </Field>
        {save.error && <p className="error">{save.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={save.isPending || !name.trim()}
            onClick={() => save.mutate()}
          >
            Crea
          </button>
        </div>
      </div>
    </dialog>
  );
}

function RenameDialog({ plan, onClose }: { plan: ReclassPlan; onClose: () => void }) {
  const queryClient = useQueryClient();
  const ref = useDialog();
  const [name, setName] = useState(plan.name);
  const save = useMutation({
    mutationFn: () =>
      apiRequest(`/controlling/plans/${plan.id}`, { method: 'PUT', body: { name } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: plansKey });
      onClose();
    },
  });
  return (
    <dialog ref={ref} className="dialog narrow" onClose={onClose}>
      <div className="stack">
        <h2>Rinomina il piano</h2>
        <Field label="Nome">
          <input value={name} maxLength={80} onChange={(event) => setName(event.target.value)} />
        </Field>
        {save.error && <p className="error">{save.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={save.isPending || !name.trim()}
            onClick={() => save.mutate()}
          >
            Salva
          </button>
        </div>
      </div>
    </dialog>
  );
}

function ImportDialog({ plan, onClose }: { plan: ReclassPlan; onClose: () => void }) {
  const queryClient = useQueryClient();
  const ref = useDialog();
  const [csv, setCsv] = useState<string | null>(null);
  const send = (text: string, dryRun: boolean) =>
    apiRequest<ReclassImportResult>(`/controlling/plans/${plan.id}/import`, {
      method: 'POST',
      body: { csv: text, dryRun },
    });
  const preview = useMutation({ mutationFn: (text: string) => send(text, true) });
  const load = useMutation({
    mutationFn: () => send(csv!, false),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: plansKey });
      onClose();
    },
  });
  const choose = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    // The Excel saves the CSV in the Windows encoding: read it as such when it is not UTF-8.
    const bytes = new Uint8Array(await file.arrayBuffer());
    let text: string;
    try {
      text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch {
      text = new TextDecoder('windows-1252').decode(bytes);
    }
    setCsv(text);
    preview.mutate(text);
  };
  const result = preview.data;
  return (
    <dialog ref={ref} className="dialog narrow" onClose={onClose}>
      <div className="stack">
        <h2>Importa CSV · {plan.name}</h2>
        <p className="muted small">
          Il file dell’Excel: CONTO;SEGNO;DESCRIZIONE;FORMULA. Le voci del file prendono il posto di
          quelle del piano, nel loro ordine; quelle che non ci sono si tolgono, o si disattivano se
          sono usate.
        </p>
        <input type="file" accept=".csv,text/csv" onChange={(event) => void choose(event)} />
        {preview.isPending && <p className="muted">Controllo del file…</p>}
        {preview.error && <p className="error">{preview.error.message}</p>}
        {result && (
          <>
            <dl className="totals">
              <div>
                <dt>Nuove</dt>
                <dd>{result.created}</dd>
              </div>
              <div>
                <dt>Aggiornate</dt>
                <dd>{result.updated}</dd>
              </div>
              <div>
                <dt>Tolte</dt>
                <dd>{result.removed}</dd>
              </div>
              <div>
                <dt>Disattivate (in uso)</dt>
                <dd>{result.deactivated}</dd>
              </div>
            </dl>
            {result.problems.length > 0 && (
              <div className="error small">
                {result.problems.slice(0, 10).map((problem) => (
                  <div key={problem}>{problem}</div>
                ))}
              </div>
            )}
          </>
        )}
        {load.error && <p className="error">{load.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={load.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={!result || result.problems.length > 0 || load.isPending}
            onClick={() => load.mutate()}
          >
            {load.isPending ? 'Attendere…' : 'Importa'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
